/**
 * Author: Claude Code
 * Version: v3.2.38
 * Modified: 2025-10-04
 *
 * AgentCard - 单个Agent信息卡片，支持史莱姆大头照显示
 */

import React, { useState } from 'react';

export interface AgentData {
  id: number;
  agentId?: string; // backend agent id for event matching
  name: string;
  status: 'active' | 'wait' | 'died';
  hp: number;
  maxHp: number;
  weapon: string;
  inventory: string[]; // 存医疗徽章（small-medkit/large-medkit）；渲染时聚合数量
  lastThinking?: string; // last LLM TL objective summary
  character: string; // emoji fallback
  colorIndex?: number; // 0-6 mapping to slime portraits (0=green,1=red,2=purple,3=blue,4=white,5=black,6=orange)
}

export interface AgentCardProps {
  agent: AgentData;
  onBet?: (agentId: number, amount: number) => void;
  userBalance?: number;
  portraits?: { [colorIndex: number]: string }; // Portrait preloaded paths
}

export const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  onBet,
  userBalance = 5000,
  portraits
}) => {
  const [showBetOptions, setShowBetOptions] = useState(false);

  const handleBet = (amount: number) => {
    if (onBet && amount <= userBalance) {
      onBet(agent.id, amount);
      setShowBetOptions(false);
    }
  };

  const getStatusColor = () => {
    switch (agent.status) {
      case 'active': return 'status-active';
      case 'wait': return 'status-wait';
      case 'died': return 'status-died';
      default: return '';
    }
  };

  const hpPercentage = (agent.hp / agent.maxHp) * 100;

  return (
    <div className={`agent-card ${getStatusColor()}`}>
      <div className="agent-card-header">
        <div className="agent-info">
          {portraits && agent.colorIndex !== undefined && portraits[agent.colorIndex] ? (
            <img src={portraits[agent.colorIndex]} alt={`Agent ${agent.colorIndex}`} className="agent-portrait" />
          ) : (
            <span className="agent-character">{agent.character}</span>
          )}
          <span className="agent-name">{agent.name}</span>
        </div>
        <div className="agent-status-badge">{agent.status.toUpperCase()}</div>
      </div>

      <div className="agent-card-body">
        {/* HP进度条 */}
        <div className="hp-section">
          <div className="hp-label">
            <span>HP</span>
            <span>{agent.hp}/{agent.maxHp}</span>
          </div>
          <div className="hp-bar-bg">
            <div
              className="hp-bar-fill"
              style={{
                width: `${hpPercentage}%`,
                backgroundColor: hpPercentage > 60 ? '#C7E3E5' : hpPercentage > 30 ? '#ff9900' : '#cc0000'
              }}
            />
          </div>
        </div>

        {/* Weapon */}
        <div className="info-row">
          <span className="info-label">Weapon:</span>
          <span className="info-value">{agent.weapon}</span>
        </div>

        {/* Inventory（仅医疗，聚合计数） */}
        {(() => {
          if (!agent.inventory || agent.inventory.length === 0) return null;
          const counts = agent.inventory.reduce((acc: Record<string, number>, item) => {
            const key = item === 'small-medkit' || item === 'large-medkit' ? item : '';
            if (!key) return acc; // 忽略非医疗
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});
          const entries = Object.entries(counts);
          if (entries.length === 0) return null;
          return (
            <div className="info-row inventory-row">
              <span className="info-label">Inventory:</span>
              <div className="inventory-items">
                {entries.map(([name, n]) => (
                  <span key={name} className="inventory-badge">{name} × {n}</span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Last Thinking */}
        <div className="info-row last-move-row">
          <span className="info-label">Last Thinking:</span>
          <span className="info-value last-move-text">{agent.lastThinking || '-'}</span>
        </div>

        {/* 🟠 橙色BET按钮 */}
        {agent.status === 'active' && (
          <>
            <button
              className="bet-button-orange"
              onClick={() => setShowBetOptions(!showBetOptions)}
            >
              🎯 BET ON THIS AGENT
            </button>

            {/* 展开下注选项 */}
            {showBetOptions && (
              <div className="bet-options">
                <div className="bet-balance">
                  Remaining: <span>{userBalance.toLocaleString()} momo</span>
                </div>
                <div className="bet-amounts">
                  <button
                    className="bet-amount-btn"
                    onClick={() => handleBet(100)}
                    disabled={userBalance < 100}
                  >
                    100
                  </button>
                  <button
                    className="bet-amount-btn"
                    onClick={() => handleBet(500)}
                    disabled={userBalance < 500}
                  >
                    500
                  </button>
                  <button
                    className="bet-amount-btn"
                    onClick={() => handleBet(1000)}
                    disabled={userBalance < 1000}
                  >
                    1000
                  </button>
                </div>
                <button
                  className="bet-cancel-btn"
                  onClick={() => setShowBetOptions(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
